import { create } from "zustand";
import { persist } from "zustand/middleware";
import type {
  EffectiveAccessContext,
  PageOverrideMode,
} from "@medicorepay/shared";
import { resolveActiveStoreSession } from "@medicorepay/shared/lib/api-client";
import {
  captureWebSession,
  commitWebSessionReplacement,
  invalidateWebAuthSession,
  persistWebAuthSession,
} from "../lib/auth-session";
import {
  TenantValidationStatus,
  tenantValidator,
} from "../lib/tenant-validator";
import { authService } from "../services/auth.service";

// Sidebar configuration types (matching API response)
interface SidebarItem {
  name: string;
  href: string;
  icon?: string;
  requiredRole?: string;
  requiredModule?: string;
}

interface SidebarGroup {
  moduleCode: string;
  title: string;
  items: SidebarItem[];
  sortOrder: number;
}

interface TenantSidebarConfig {
  groups: SidebarGroup[];
}

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role?: string;
  roleName?: string | null;
  roleId?: string | null;
  permissions?: string[];
  permVersion?: number;
  accessVersion?: number;
  tenantId: string;
  isCompositeDealer?: boolean;
  storeId?: string;
  stores?: Array<{ id: string; name: string }>;
  phone?: string;
  enabledModules?: string[];
  pageOverrides?: Partial<Record<string, PageOverrideMode>>;
  disabledPageKeys?: string[];
  accessContext?: EffectiveAccessContext;
  sidebarConfig?: TenantSidebarConfig;
  impersonation?: {
    logId: string;
    adminId: string;
    expiresAt: string;
  };
}

// Re-export types for use in other components
export type { SidebarGroup, SidebarItem, TenantSidebarConfig };

interface TenantValidation {
  isValid: boolean;
  lastValidation: string | null;
  daysOffline: number;
  requiresOnlineValidation: boolean;
  showOfflineWarning: boolean;
  remainingDays: number;
  error?: string;
}

interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  hasHydrated: boolean;
  selectedStoreId: string | null;
  selectedCounterId: string | null;
  tenantValidation: TenantValidation | null;

  // Actions
  setAuth: (user: User, token: string, refreshToken: string) => void;
  replaceSessionUser: (
    user: User,
    accessToken?: string,
    refreshToken?: string,
  ) => void;
  applyAccessContext: (context: EffectiveAccessContext) => void;
  clearAuth: () => void;
  logout: () => Promise<void>;
  setLoading: (loading: boolean) => void;
  setHasHydrated: (hydrated: boolean) => void;
  setSelectedStore: (storeId: string) => Promise<void>;
  switchStore: (storeId: string) => Promise<void>;
  setSelectedCounter: (counterId: string | null) => void;
  validateTenantSession: () => Promise<TenantValidationStatus>;
  setTenantValidation: (status: TenantValidationStatus) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      hasHydrated: false,
      selectedStoreId: null,
      selectedCounterId: null,
      tenantValidation: null,

      setAuth: (user, token, refreshToken) => {
        persistWebAuthSession(token, refreshToken);
        if (user.permVersion !== undefined) {
          localStorage.setItem("permVersion", String(user.permVersion));
        }
        set((state) => ({
          user,
          token,
          refreshToken,
          isAuthenticated: true,
          // Auto-select store if user has storeId or only one store
          selectedStoreId:
            user.storeId ||
            (user.stores?.length === 1
              ? user.stores[0].id
              : state.selectedStoreId),
        }));
      },

      replaceSessionUser: (user, accessToken, refreshToken) => {
        if (user.permVersion !== undefined) {
          localStorage.setItem("permVersion", String(user.permVersion));
        }
        set((state) => ({
          user,
          token: accessToken ?? state.token,
          refreshToken: refreshToken ?? state.refreshToken,
          selectedStoreId: user.storeId ?? state.selectedStoreId,
        }));
      },

      applyAccessContext: (context) => {
        localStorage.setItem("permVersion", String(context.permVersion));
        set((state) => ({
          user: state.user
            ? {
                ...state.user,
                permVersion: context.permVersion,
                accessVersion: context.accessVersion,
                permissions: context.permissions,
                enabledModules: context.modules,
                pageOverrides: context.pageOverrides,
                disabledPageKeys: context.disabledPageKeys,
                accessContext: context,
              }
            : null,
        }));
      },

      clearAuth: () => {
        tenantValidator.clearValidationData();
        invalidateWebAuthSession();
        set({
          user: null,
          token: null,
          refreshToken: null,
          isAuthenticated: false,
          selectedStoreId: null,
          selectedCounterId: null,
          tenantValidation: null,
        });
        localStorage.removeItem("user");
        localStorage.removeItem("permVersion");
        localStorage.removeItem("auth-storage");
      },

      logout: async () => {
        const remoteLogout = authService
          .logout(get().token)
          .catch(() => undefined);
        get().clearAuth();
        await remoteLogout;
      },

      setLoading: (loading) => set({ isLoading: loading }),

      setHasHydrated: (hydrated) => set({ hasHydrated: hydrated }),

      setSelectedStore: (storeId) => get().switchStore(storeId),

      switchStore: async (storeId) => {
        await authService.recoverPendingSession();
        await authService.runSessionMutation(async () => {
          const state = get();
          if (storeId === state.selectedStoreId) return;

          const snapshot = captureWebSession();
          const response = await authService.selectActiveStore(storeId);
          const currentSession = captureWebSession();
          if (currentSession.generation !== snapshot.generation) {
            throw new Error(
              "Session changed while the store was being selected",
            );
          }
          const resolved = resolveActiveStoreSession(
            response,
            storeId,
            currentSession.refreshToken,
          );
          if (
            !commitWebSessionReplacement(
              currentSession,
              resolved.accessToken,
              resolved.refreshToken,
            )
          ) {
            throw new Error(
              "Session changed while the store was being selected",
            );
          }
          if (resolved.user.permVersion !== undefined) {
            localStorage.setItem(
              "permVersion",
              String(resolved.user.permVersion),
            );
          }
          set({
            user: resolved.user,
            token: resolved.accessToken,
            refreshToken: resolved.refreshToken,
            selectedStoreId: resolved.storeId,
            selectedCounterId: null,
          });
        });
      },

      setSelectedCounter: (counterId) => set({ selectedCounterId: counterId }),

      validateTenantSession: async () => {
        const status = await tenantValidator.validateOnAppStart();
        get().setTenantValidation(status);

        if (!status.isValid) {
          void get().logout();
        } else {
          tenantValidator.startPeriodicRevalidation();
          tenantValidator.subscribe((newStatus) => {
            get().setTenantValidation(newStatus);
            if (!newStatus.isValid) {
              void get().logout();
            }
          });
        }

        return status;
      },

      setTenantValidation: (status) =>
        set({
          tenantValidation: {
            isValid: status.isValid,
            lastValidation: status.lastValidation?.toISOString() || null,
            daysOffline: status.daysOffline,
            requiresOnlineValidation: status.requiresOnlineValidation,
            showOfflineWarning: tenantValidator.shouldShowOfflineWarning(),
            remainingDays: tenantValidator.getRemainingOfflineDays(),
            error: status.error,
          },
        }),
    }),
    {
      name: "auth-storage",
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        selectedStoreId: state.selectedStoreId,
        selectedCounterId: state.selectedCounterId,
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true);
      },
    },
  ),
);

/** Keep another browser tab's UI scope aligned with its persisted JWT scope. */
export function syncExternalAuthStorage(serializedState: string | null): void {
  if (!serializedState) {
    tenantValidator.clearValidationData();
    useAuthStore.setState({
      user: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      selectedStoreId: null,
      selectedCounterId: null,
      tenantValidation: null,
    });
    return;
  }

  try {
    const persisted = JSON.parse(serializedState) as {
      state?: Partial<AuthState>;
    };
    const external = persisted.state;
    if (!external?.isAuthenticated || !external.user || !external.token) {
      return;
    }
    useAuthStore.setState({
      user: external.user,
      token: external.token,
      refreshToken: external.refreshToken ?? null,
      isAuthenticated: true,
      selectedStoreId:
        external.user.storeId ?? external.selectedStoreId ?? null,
      selectedCounterId: null,
    });
  } catch {
    // Ignore malformed external storage; the current tab keeps its safe state.
  }
}
